<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useUpgradeCooldown, useUpgradeExpiration } from '@/composables/game/upgrade'
import { type PlayerUpgrade } from '@/types/shared/player-config'
import type { PlayerStats } from '@/types/shared/player'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import LockIcon from '@/components/icons/LockIcon.vue'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { T, useTranslate } from '@tolgee/vue'
import useIsScreenSmallerThan from '@/composables/helpers/is-screen-smaller-than'
import { DateTime } from 'luxon'
import { twMerge } from 'tailwind-merge'
import FingerUpGif from '@/assets/images/gifs/finger-up.gif'
import { useOnboardingFingerEmojis } from '@/composables/game/onboarding'
import ThePremium from '@/components/ThePremium.vue'

const props = withDefaults(defineProps<{
	upgrade: PlayerUpgrade
	player: PlayerStats
	upgradeList: PlayerUpgrade[]
	isSpecial?: boolean
	isHighlight?: boolean
	bgColor?: string
	canUpgradeForStars: boolean
	index: number
	isInBattleConflict?: boolean
	isNew: boolean
}>(), {
  isHighlight: false,
  isSpecial: false,
  bgColor: undefined,
  isInBattleConflict: false,
})

const emit = defineEmits<{
	(e: 'cardClick'): void
}>()

const isScreenSmallerThan440 = useIsScreenSmallerThan(440)
const {
  formattedTime: formattedExpirationTime,
  remainedTime: remainedExpirationTime,
} = useUpgradeExpiration(computed(() => props.upgrade))

const { formattedTime, remainedTime, canFastForward } = useUpgradeCooldown(computed(() => props.upgrade))
const { t } = useTranslate()

const { canShowOnboardingEmoji, showOnboardingEmojiCard } = useOnboardingFingerEmojis()

const cooldownOverlay = ref<HTMLElement | null>(null)

const profitOnCard = computed(() =>
  `+${formatNumberFullTruncate(props.upgrade.profitDelta)}`,
)

const isExpired = computed(() => {
  if (!props.upgrade.expiresOn) return false
  return DateTime.utc().toSeconds() - props.upgrade.expiresOn > 0
})

const upgradeLock = computed(() => props.upgrade.requirements.upgrade)
const referralLock = computed(() => props.upgrade.requirements.referrals)
const newReferralLock = computed(() => {
  const lastReferralTimestamp = props.player.referrals.lastReferral ?? 0
  const since = props.upgrade.requirements.newReferrals?.since
  return since && lastReferralTimestamp < since && !props.upgrade.level
})

const isLocked = computed(() =>
  !isExpired.value && (newReferralLock.value ||
			(upgradeLock.value !== undefined && (findUpgradeById(upgradeLock.value.id)?.level ?? 0) < upgradeLock.value.level) ||
			(referralLock.value !== undefined && (props.player.friends.count + props.player.friends.virtualCount) < referralLock.value.count)
  ),
)

const hasReachedMaxLevel = computed(() => props.upgrade.price === 0)

const _canFastForward = computed(() => canFastForward.value && !hasReachedMaxLevel.value)

const isDisabled = computed(() => isExpired.value || hasReachedMaxLevel.value || props.isInBattleConflict)
const isTooltipDisabled = computed(() => !props.isInBattleConflict || isExpired.value)

const insufficientFunds = computed(() => props.player.fortuneWheel.prizes.freeUpgrades === 0 && props.player.coins < props.upgrade.price)

const hasCooldown = computed(() => props.upgrade.cooldown > 0 && formattedTime.value !== undefined)

const showUpgradeForStars = computed(() => !hasReachedMaxLevel.value && props.canUpgradeForStars && insufficientFunds.value && !hasCooldown.value && !isLocked.value && !isDisabled.value)

const cooldownForAnimation = ref(0)
const cooldownAnimationInitialStartDashoffset = ref(0)
const elapsedTimeForAnimation = ref(0)
const cooldownPercentage = computed(() => (elapsedTimeForAnimation.value / props.upgrade.cooldown) * 100)
const cooldownStyle = computed(() => {
  return {
    '--cooldown-seconds': `${cooldownForAnimation.value}s`,
    '--pgPercentage': cooldownPercentage.value,
    '--stroke-dashoffset': `${cooldownAnimationInitialStartDashoffset.value}`,
    '--size': `${56}px`,
    '--dot-size-multiplier': `${1}`,
  }
})

const cardStyle = computed(() => {
  if (props.isHighlight && props.upgrade.highlight?.backgroundImageUrl) {
    return {
      background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(44, 44, 46, 0.88) 81%), url(${props.upgrade.highlight.backgroundImageUrl}) no-repeat`,
      'backgroundSize': 'cover',
      '-webkit-filter': isDisabled.value ? 'grayscale(100%)' : undefined,
      filter: isDisabled.value ? 'grayscale(100%)' : undefined,
    }
  }
  if (props.isSpecial && props.bgColor) {
    return {
      'background': `linear-gradient(180deg, ${props.bgColor} 0%, #1C1C1E 100%)`,
    }
  }

  return undefined

})

function findUpgradeById(upgradeId: string) {
  return props.upgradeList.find((listItem) => listItem.id === upgradeId)
}

function resetCooldown() {
  cooldownForAnimation.value = remainedTime.value
  elapsedTimeForAnimation.value = props.upgrade.cooldown - (cooldownForAnimation.value)
  cooldownAnimationInitialStartDashoffset.value = -(elapsedTimeForAnimation.value / props.upgrade.cooldown) * 100
}

function onCardClick() {
  if (isLocked.value || isDisabled.value) return
  emit('cardClick')
}

watch(hasCooldown, (newVal) => {
  if (newVal) {
    resetCooldown()
    return
  }
})

onMounted(() => {
  if (hasCooldown.value) {
    resetCooldown()
  }
})
</script>

<template>
  <VTooltip
    placement="top"
    :distance="-16"
    :class="twMerge(
      'w-full rounded-xl flex flex-col min-w-0',
      isHighlight && 'col-span-2 items-center',
    ) "
    popper-class="upgrade-card-tooltip"
    strategy="fixed"
    :disabled="isTooltipDisabled"
  >
    <template #popper>
      This card is in battle! Keep upgrading your chosen card to dominate and earn rewards! ⚔️💰
    </template>
    <div
      :class="twMerge(
        'flex-1 w-full rounded-xl flex flex-col min-w-0 bg-dark-bottom-layer relative transition-all',
        (isLocked || isDisabled) && 'opacity-[68%]',
        isHighlight && 'col-span-2 items-center !opacity-100',
        isHighlight && hasCooldown && 'rounded-tr-[32px]',
      ) "
      :style="cardStyle"
      @click="showOnboardingEmojiCard = false; onCardClick()"
    >
      <div
        v-if="isNew"
        class="border border-white/[.38] shadow-new-badge absolute left-2 top-2 bg-green-primary w-[41px] py-px rounded text-xs text-tropee-black font-bold uppercase flex items-center justify-center z-[1]"
      >
        new
      </div>
      <LockIcon
        v-if="isLocked && isHighlight"
        class="absolute top-3 right-3 w-6 h-6 min-w-[24px] text-white"
      />
      <img
        v-if="index === 0 && canShowOnboardingEmoji && showOnboardingEmojiCard"
        class="pointer-events-none absolute bottom-0 left-1/2 translate-x-[calc(-50%+30px)]  size-[88px] rotate-[-45deg] object-cover z-[1]"
        :src="FingerUpGif"
      >
      <div
        :class="twMerge('p-3 flex items-start justify-between', isHighlight && 'pt-6', _canFastForward && 'pr-2')"
      >
        <div
          class="relative"
          :class="[{'rounded-full': hasCooldown}, `h-[56px] ${isHighlight ? 'w-auto' : 'w-[56px]'}`]"
        >
          <img
            :class="twMerge('w-auto h-full object-cover z-[1]', hasCooldown && !isHighlight && 'rounded-full w-full relative')"
            :src="isHighlight ? upgrade.highlight?.logoUrl : upgrade.imageUrl"
          >
          <template v-if="hasCooldown && !isHighlight">
            <svg
              class="cooldown-svg"
              viewBox="0 0 36 36"
              :style="cooldownStyle"
            >
              <path
                id="cooldown-path"
                class="cooldown-border"
                d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
                transform="rotate(-90 18 18)"
              />
            </svg>
            <div
              ref="cooldownOverlay"
              class="cooldown-overlay absolute top-0 left-0 w-full h-full rounded-full z-[2] flex items-center justify-center bg-black/[.45]"
              :style="cooldownStyle"
            />
            <div
              :class="twMerge('absolute top-1/2 left-1/2 z-[11] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center',
                              remainedTime < 3600 ? 'gap-y-1' : ' gap-y-0.5')"
            >
              <ClockIcon class="w-4 h-4 min-w-[16px]" />
              <span
                :class="twMerge('font-medium -tracking-[0.28px]',
                                (remainedTime < 3600 ? 'text-sm' : 'text-[10px] leading-3')
                )"
              >{{
                formattedTime
              }}</span>
            </div>
          </template>
        </div>
        <LockIcon
          v-if="isLocked && !isHighlight"
          class="w-6 h-6 min-w-[24px] text-white"
        />
        <div
          v-if=" showUpgradeForStars || _canFastForward"
          class="absolute top-1.5 z-[20]"
          :class="isHighlight ? 'left-1.5' : 'right-1.5'"
        >
          <div
            class="flex items-center justify-center gap-x-1.5 p-2 bg-dark-top-layer rounded-md relative overflow-hidden w-full"
          >
            <ThePremium
              image-class="size-3 min-w-[12px]"
              :with-tooltip="false"
            />
            <span class="text-xs leading-3 font-semibold tracking-tight">
              {{ showUpgradeForStars ? t('generic.upgrade', 'Upgrade') : t('generic.upgrade.speed_up', 'Speed Up') }}
            </span>
            <i
              class="shiny"
            />
          </div>
        </div>
      </div>
      <div
        v-if="hasCooldown && isHighlight"
        class="absolute top-1 right-1 w-14 h-14"
      >
        <div class="relative w-full h-full">
          <svg
            class="cooldown-svg"
            viewBox="0 0 36 36"
            :style="cooldownStyle"
          >
            <path
              id="cooldown-path"
              class="cooldown-border"
              d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
              transform="rotate(-90 18 18)"
            />
          </svg>
          <div
            ref="cooldownOverlay"
            class="cooldown-overlay absolute top-0 left-0 w-14 h-14 rounded-full z-[2] flex items-center justify-center bg-black/[.45]"
            :style="cooldownStyle"
          />
          <div
            :class="twMerge('absolute top-1/2 left-1/2 z-[11] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center', remainedTime < 3600 ? 'gap-y-1' : ' gap-y-0.5')"
          >
            <ClockIcon class="w-4 h-4 min-w-[16px]" />
            <span
              :class="twMerge('font-medium -tracking-[0.28px]', remainedTime < 3600 ? 'text-sm' : 'text-[10px] leading-3')"
            >{{
              formattedTime
            }}</span>
          </div>
        </div>
      </div>
      <div
        class="flex flex-col px-3 min-w-0 flex-1"
        :class="isHighlight ? 'pb-3 w-full items-center' : 'pb-2'"
      >
        <p
          class="max-h-[36px] line-clamp-2 text-sm leading-[18px] font-semibold"
          :class="isHighlight ? 'text-center' : 'flex-1'"
        >
          {{ upgrade.name }}
        </p>
        <p
          v-if="isHighlight"
          class="text-center text-xs font-medium text-white/60"
        >
          {{ upgrade.highlight?.description }}
        </p>
        <div
          class="flex items-center mt-3"
        >
          <div
            class="text-white/60 text-xs whitespace-nowrap font-medium"
          >
            <T
              v-if="isScreenSmallerThan440 && !isHighlight"
              key-name="generic.profit_per_hour_short"
              default-value="Trading/hour"
            />
            <T
              v-else
              key-name="generic.profit_per_hour"
              default-value="Trading per hour"
            />
          </div>
          <div
            class="flex items-center"
            :class="[(insufficientFunds || isLocked || (isDisabled && !isHighlight) || (hasCooldown && !isHighlight) || upgrade.level === 0) && 'opacity-60', isHighlight ? 'ml-1' : 'ml-auto']"
          >
            <CoinIcon
              v-if="!isScreenSmallerThan440 && !isSpecial"
              class="w-[10px] h-[16px] min-w-[10px] mr-1.5 text-white"
            />
            <div
              class="text-sm font-semibold -tracking-[0.28px] text-white"
            >
              {{ profitOnCard }}
            </div>
          </div>
        </div>
      </div>
      <div class="w-full h-px bg-white/[.08]" />
      <div
        class="flex items-center justify-between gap-x-2 px-3 min-w-0 w-full"
        :class="isSpecial ? 'py-3 min-h-[44px]' : 'py-2 min-h-[34px]'"
      >
        <div
          class="text-xs leading-[18px] font-bold -tracking-[0.24px]"
          :class="{'text-white/60': (insufficientFunds || (hasCooldown && !isHighlight) || upgrade.level === 0)}"
        >
          <T
            key-name="generic.level"
            default-value="Level {level}"
            :params="{ level: upgrade.level }"
          />
        </div>
        <div
          v-if="isHighlight && remainedExpirationTime"
          class="flex gap-x-1 items-center"
          :class="newReferralLock && 'text-white/60'"
        >
          <ClockIcon class="w-4 h-4 min-w-[16px] text-green-primary" />
          <span class="text-sm font-semibold -tracking-[0.28px]">{{ formattedExpirationTime }}</span>
        </div>
        <div
          v-if="isDisabled"
          class="ml-auto text-sm text-light-white -tracking-[0.28px] font-semibold"
        >
          <T
            v-if="hasReachedMaxLevel"
            key-name="cards.max_level_reached"
            default-value="Max level reached"
          />
          <T
            v-else-if="isExpired"
            key-name="cards.expired"
            default-value="Expired"
          />
        </div>
        <div
          v-else-if="isLocked"
          class="min-w-0 text-xs leading-4 -tracking-[0.24px] flex items-center justify-end text-right break-words"
          :class="!isHighlight && 'flex-1'"
        >
          <span
            v-if="referralLock"
            class="font-semibold"
          >
            Add {{ referralLock.count }} friend{{ referralLock.count > 1 ? 's' : '' }}
          </span>
          <span
            v-else-if="upgradeLock"
            class="flex-1 ml-auto font-semibold max-w-[84px]"
          >
            <T
              key-name="cards.locked_by_card"
              default-value="{card_name} lvl {level}"
            >
              <template #card_name>
                <T
                  :key-name="`card.${upgradeLock?.id}.name`"
                  :default-value="props.upgradeList.find(uItem => uItem.id === upgradeLock?.id)?.name"
                />
              </template>
              <template #level>
                <span class="font-semibold">{{ upgradeLock.level }}</span>
              </template>
            </T>
          </span>
          <span
            v-else-if="newReferralLock"
            class="font-semibold text-white/60"
          >
            <T
              key-name="cards.locked_by_card"
              default-value="Refer 1 friend"
            /></span>
        </div>
        <div
          v-else
          class="flex items-center gap-x-1.5"
          :class="(insufficientFunds || (hasCooldown && !isHighlight)) && 'opacity-60'"
        >
          <CoinIcon
            class="w-[13px] h-5 min-w-[13px] text-white"
          />
          <div
            class="text-sm font-semibold -tracking-[0.28px] "
            :class="insufficientFunds || (hasCooldown && !isHighlight) ? 'text-white' : 'text-green-primary'"
          >
            {{ formatNumberFullTruncate(upgrade.price) }}
          </div>
        </div>
      </div>
    </div>
  </VTooltip>
</template>

<style scoped lang="scss">
@import '@/assets/styles/cooldown.scss';

i.shiny {
	/*   positioning */
	position: absolute;
	opacity: 0;
	top: -2px;
	left: -16px;

	/*   gradient   */
	background: -moz-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.03) 99%, rgba(255, 255, 255, 0) 100%); /* FF3.6+ */
	background: -webkit-gradient(linear, left top, right top, color-stop(0%, rgba(255, 255, 255, 0)), color-stop(1%, rgba(255, 255, 255, 0.03)), color-stop(30%, rgba(255, 255, 255, 0.6)), color-stop(50%, rgba(255, 255, 255, 0.6)), color-stop(70%, rgba(255, 255, 255, 0.4)), color-stop(99%, rgba(255, 255, 255, 0.03)), color-stop(100%, rgba(255, 255, 255, 0))); /* Chrome,Safari4+ */
	background: -webkit-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.03) 99%, rgba(255, 255, 255, 0) 100%); /* Chrome10+,Safari5.1+ */
	background: -o-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.03) 99%, rgba(255, 255, 255, 0) 100%); /* Opera 11.10+ */
	background: -ms-linear-gradient(left, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.03) 99%, rgba(255, 255, 255, 0) 100%); /* IE10+ */
	background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.03) 1%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, rgba(255, 255, 255, 0.03) 99%, rgba(255, 255, 255, 0) 100%); /* W3C */
	filter: progid:DXImageTransform.Microsoft.gradient(startColorstr='#00ffffff', endColorstr='#00ffffff', GradientType=1); /* IE6-9 */

	/*  forming the shine element
			play around with the width, skew and gradient to get different effects
		*/
	width: 25%;
	z-index: 1;
	height: calc(100% + 4px);
	transform: skew(-20deg, 0deg);
	-webkit-transform: skew(-20deg, 0deg);
	-moz-transform: skew(-20deg, 0deg);
	-ms-transform: skew(-20deg, 0deg);
	-o-transform: skew(-20deg, 0deg);

	/*  animating it  */
	animation-name: move;
	animation-duration: 1.8s;
	animation-iteration-count: infinite;
	animation-delay: 0.3s;
	-webkit-animation-name: move;
	-webkit-animation-duration: 1.8s;
	-webkit-animation-iteration-count: infinite;
	-webkit-animation-delay: 0.3s;
	-moz-transform: skew(-20deg, 0deg);
	-moz-animation-name: move;
	-moz-animation-duration: 1.8s;
	-moz-animation-iteration-count: infinite;
	-moz-animation-delay: 0.3s;
	-ms-transform: skew(-20deg, 0deg);
	-ms-animation-name: move;
	-ms-animation-duration: 1.8s;
	-ms-animation-iteration-count: infinite;
	-ms-animation-delay: 0.3s;
	-o-transform: skew(-20deg, 0deg);
	-o-animation-name: move;
	-o-animation-duration: 1.8s;
	-o-animation-iteration-count: infinite;
	-o-animation-delay: 0.3s;
}

/*  */
@keyframes move {
	0% {
		left: -16px;
		opacity: 0;
	}
	50% {
		left: -16px;
		opacity: 0;
	}
	51% {
		left: -16px;
		opacity: 0.4;
	}
	70% {
		opacity: 0.4
	}
	90% {
		opacity: 0.2
	}
	100% {
		left: calc(100% + 4px)
	}
}

@-webkit-keyframes move {
	0% {
		left: -16px;
		opacity: 0.4;
	}
	50% {
		left: -16px;
		opacity: 0;
	}
	51% {
		left: -16px;
		opacity: 0.4;
	}
	70% {
		opacity: 0.4
	}
	90% {
		opacity: 0.2
	}
	100% {
		left: calc(100% + 4px)
	}
}
</style>
<style lang="scss">
.upgrade-card-tooltip.v-popper--theme-tooltip {
	.v-popper__inner {
		@apply max-w-[220px] text-left #{!important};
	}
}

.shadow-new-badge {
	box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.44);
}
</style>
